import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import WelcomeStep from "./WelcomeStep";
import PainPointStep from "./PainPointStep";
import TimeStep from "./TimeStep";
import RevenueStep from "./RevenueStep";
import AuthorityStep from "./AuthorityStep";
import ResultsStep from "./ResultsStep";
import ProgressBar from "./ProgressBar";

export interface WizardState {
  painPoint: string;
  hoursPerWeek: number;
  revenuePotential: string;
  isDecisionMaker: string;
}

const LeadQualificationWizard = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [leadScore, setLeadScore] = useState(0);
  const [answers, setAnswers] = useState<WizardState>({
    painPoint: "",
    hoursPerWeek: 0,
    revenuePotential: "",
    isDecisionMaker: "",
  });

  const totalSteps = 6;

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const updateAnswer = (key: keyof WizardState, value: string | number) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const updateScore = (points: number) => {
    setLeadScore((prev) => prev + points);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(255 255 255 / 0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Progress Bar - Only show after welcome step */}
      {currentStep > 0 && currentStep < totalSteps - 1 && (
        <ProgressBar currentStep={currentStep - 1} totalSteps={4} />
      )}

      {/* Main Content */}
      <div className="w-full max-w-2xl relative z-10">
        <AnimatePresence mode="wait">
          {currentStep === 0 && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <WelcomeStep onNext={handleNext} />
            </motion.div>
          )}

          {currentStep === 1 && (
            <motion.div
              key="painpoint"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <PainPointStep
                onNext={handleNext}
                onBack={handleBack}
                updateAnswer={updateAnswer}
                updateScore={updateScore}
                currentAnswer={answers.painPoint}
              />
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div
              key="time"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <TimeStep
                onNext={handleNext}
                onBack={handleBack}
                updateAnswer={updateAnswer}
                updateScore={updateScore}
                currentAnswer={answers.hoursPerWeek}
              />
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div
              key="revenue"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <RevenueStep
                onNext={handleNext}
                onBack={handleBack}
                updateAnswer={updateAnswer}
                updateScore={updateScore}
                currentAnswer={answers.revenuePotential}
              />
            </motion.div>
          )}

          {currentStep === 4 && (
            <motion.div
              key="authority"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <AuthorityStep
                onNext={handleNext}
                onBack={handleBack}
                updateAnswer={updateAnswer}
                updateScore={updateScore}
                currentAnswer={answers.isDecisionMaker}
              />
            </motion.div>
          )}

          {currentStep === 5 && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <ResultsStep leadScore={leadScore} onBack={handleBack} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LeadQualificationWizard;
